/**
 * `ACTION_TYPE_LABEL` / `GRADE_LABEL` — sentence-case (DES-41) display labels for
 * `growth_actions.action_type` and `.evidence_grade`, extracted from
 * the retired `src/components/shared/suggestion-card.tsx` and
 * `src/components/shared/evidence-grade-badge.tsx` (T2, extract-shared) into this server-safe home. No
 * `'use client'`, no React import — both are plain `Record` lookups, so a server route (the digest
 * cron, T5) or a server component (the Overview server section, T6) can import them without pulling
 * a component's client boundary along (ARC-01). `evidence-grade-badge.tsx` still imports
 * `ACTION_TYPE_LABEL`/`GRADE_LABEL` from here — this is their one and only definition (ARC-18).
 */
import type { DetectorActionType } from '@/lib/detectors/types'
import type { EvidenceGrade } from '@/lib/growth/evidence/types'

// Sentence case (DES-41) — a plain category label for `Tag`, distinct from `EvidenceGradeBadge`'s
// trust state.
export const ACTION_TYPE_LABEL: Record<DetectorActionType, string> = {
  create: 'Create',
  consolidate: 'Consolidate',
  remove: 'Remove',
  correct: 'Correct',
  distribute: 'Distribute',
  link: 'Link',
  'technical-change': 'Technical change',
}

/**
 * The plain-language category a Plan card shows for its action type (`growth-plan-redesign` AC-3) —
 * what a founder with no SEO background would call the change. Deliberately a second map, not a
 * rewrite of `ACTION_TYPE_LABEL` above: the weekly digest still prints that one's words.
 */
export const ACTION_CATEGORY_LABEL: Record<DetectorActionType, string> = {
  create: 'New content',
  consolidate: 'Merge pages',
  remove: 'Remove page',
  correct: 'Fix content',
  distribute: 'Outreach',
  link: 'Internal links',
  'technical-change': 'Technical fix',
}

// DETECTORS.md §2.4 / design-spec.md:313, word for word — "Unproven" is load-bearing for
// `hypothesis`; do not paraphrase it at a call site.
export const GRADE_LABEL: Record<EvidenceGrade, string> = {
  measured: 'Measured on your data',
  established_practice: 'Established practice',
  hypothesis: 'Unproven — hypothesis',
}

/**
 * The design's sentence for a suggestions list no ranking pass has reached (`isRankingUnavailable`;
 * owner, 2026-10-03). True because the read orders an unranked list newest first
 * (`fetchGrowthActions`: `created_at desc` breaks the shared NULL tie). The Suggestions header and
 * the empty Plan's top suggestions both print it — this is its one definition (ARC-18).
 */
export const SUGGESTIONS_DESCRIPTION_UNRANKED =
  'Newest first — ranking is unavailable, so this is the order these arrived in.'

/**
 * {@link SUGGESTIONS_DESCRIPTION_UNRANKED} over a list whose main constraint's work leads (R6.15
 * B5): the constraint first, then newest first within each group — still true of the read, since each
 * group is ordered `created_at desc` over the shared NULL rank (design board `R615`).
 */
export const SUGGESTIONS_DESCRIPTION_UNRANKED_CONSTRAINT =
  'Main constraint first, then newest first — ranking is unavailable, so within each group this is the order these arrived in.'
